import { STUDIO_CANVAS_RECIPES } from '../../studio/canvas-recipes';
import { CsRecipe, CsRecipeInput, CsRecipeInputOption } from '../../../../common/models/content-studio.model';

/**
 * Controls this build adds to recipes, in code rather than in the seed.
 *
 * Recipe definitions come from `cs_recipes`, which the API upserts from its
 * seed on every boot — and every environment shares that collection. A control
 * added to the seed is therefore live for every vendor the moment any API boots
 * with it, whether or not the worker that honours it has shipped. So controls
 * still being built are layered on here, and move into the seed with the
 * deploy that ships the worker behind them.
 *
 * Mirrored by the API's planner schema (`get_recipe_schema`) so the agent can
 * set them too. Keep the option values in step with the worker
 * (`premium-render.ts`, `video-pipeline.ts`).
 */

export const QUALITY_INPUT: CsRecipeInput = {
    key: 'quality',
    label: 'Quality',
    type: 'select',
    required: false,
    default: 'premium',
    options: [
        { value: 'premium', label: 'Premium · 2K, richer scenes' },
        { value: 'standard', label: 'Standard · fast draft' },
    ],
};

export const VIDEO_ENGINE_INPUT: CsRecipeInput = {
    key: 'videoEngine',
    label: 'Engine',
    type: 'select',
    required: false,
    default: 'fast',
    options: [
        { value: 'fast', label: 'Fast · about a minute' },
        { value: 'cinematic', label: 'Cinematic · slower, richer light' },
    ],
};

/**
 * Detail shot — a macro of the material from its own photograph (worker
 * `detail-shot.ts`). A choice on Product Photography rather than a recipe,
 * because it is one more way to photograph the product.
 */
export const DETAIL_INPUTS: CsRecipeInput[] = [
    {
        key: 'shot',
        label: 'Shot',
        type: 'select',
        required: false,
        default: 'catalog',
        options: [
            { value: 'catalog', label: 'Product shot' },
            { value: 'detail', label: 'Detail close-up (texture, macro)' },
        ],
    },
    {
        // Pose the close-up yourself in the 3D studio, or describe it. Posed
        // needs a 3D model, so it is offered on furniture and rugs only.
        key: 'detailCamera',
        label: 'Camera',
        type: 'select',
        required: false,
        default: 'preset',
        appliesTo: ['model3d'],
        showWhen: { key: 'shot', equals: ['detail'] },
        options: [
            { value: 'preset', label: 'Described (angle below)' },
            { value: 'studio', label: 'Pose it in 3D' },
        ],
    },
    {
        key: 'detailFocus',
        label: 'Focus on',
        type: 'select',
        required: false,
        default: 'texture',
        showWhen: { key: 'shot', equals: ['detail'] },
        options: [
            { value: 'texture', label: 'The texture, filling the frame' },
            { value: 'edge', label: 'An edge or border' },
            { value: 'corner-fold', label: 'A folded corner / loose piece' },
            { value: 'hand', label: 'A hand touching it' },
        ],
    },
    {
        key: 'detailLight',
        label: 'Light',
        type: 'select',
        required: false,
        default: 'raking-sun',
        showWhen: { key: 'shot', equals: ['detail'] },
        options: [
            { value: 'raking-sun', label: 'Low sun raking across' },
            { value: 'soft-window', label: 'Soft window light' },
            { value: 'hard-sun', label: 'Hard sun, crisp shadows' },
            { value: 'warm-lamp', label: 'Warm lamplight' },
            { value: 'cool-daylight', label: 'Cool daylight' },
        ],
    },
    {
        key: 'detailAngle',
        label: 'Angle',
        type: 'select',
        required: false,
        default: 'grazing',
        showWhen: { key: 'shot', equals: ['detail'] },
        options: [
            { value: 'grazing', label: 'Low, grazing' },
            { value: 'oblique', label: '45° oblique' },
            { value: 'top-down', label: 'Straight down' },
        ],
    },
];

const EXTRA_CAMERA_MOVES: CsRecipeInputOption[] = [
    { value: 'truck-left', label: 'Slide left (slider)' },
    { value: 'truck-right', label: 'Slide right (slider)' },
    { value: 'static', label: 'Still camera (light only)' },
];

const EXTRA_LIGHTING: CsRecipeInputOption[] = [
    { value: 'sun-drift', label: 'Sunlight drifting across' },
    { value: 'cloud-pass', label: 'A cloud passes the sun' },
];

/** Recipes whose room render can run on the premium engine. */
export const PREMIUM_RECIPES = new Set(['lifestyle-scene', 'product-closeup', 'from-reference']);

function withOptions(input: CsRecipeInput, extra: CsRecipeInputOption[]): CsRecipeInput {
    const have = new Set((input.options || []).map((o) => o.value));
    return { ...input, options: [...(input.options || []), ...extra.filter((o) => !have.has(o.value))] };
}

export function extendRecipe(recipe: CsRecipe): CsRecipe {
    const inputs = [...(recipe.inputs || [])];
    const has = (key: string) => inputs.some((i) => i.key === key);
    if (PREMIUM_RECIPES.has(recipe.slug) && !has('quality')) {
        inputs.push(QUALITY_INPUT);
    }
    if (recipe.slug === 'product-closeup' && !has('shot')) {
        inputs.unshift(...DETAIL_INPUTS);
    }
    if (recipe.slug === 'image-to-video') {
        for (let i = 0; i < inputs.length; i++) {
            if (inputs[i].key === 'cameraMove') inputs[i] = withOptions(inputs[i], EXTRA_CAMERA_MOVES);
            if (inputs[i].key === 'lightingTransition') inputs[i] = withOptions(inputs[i], EXTRA_LIGHTING);
        }
        if (!has('videoEngine')) inputs.push(VIDEO_ENGINE_INPUT);
    }
    return { ...recipe, inputs };
}

export function extendRecipes(recipes: CsRecipe[]): CsRecipe[] {
    const list = (recipes || []).map(extendRecipe);
    // The Studio's shots, so a board opened on the canvas can show and re-run them.
    for (const r of STUDIO_CANVAS_RECIPES) if (!list.some((x) => x.slug === r.slug)) list.push(r);
    return list;
}

/**
 * Price factor for the controls above. Mirrors the API's `codeMultiplier`.
 *
 * Premium is only charged where the premium engine actually runs — a room
 * built around a 3D capture. Routes that keep the vendor's photograph or
 * shoot a studio sweep never reach it, so they are not charged for it.
 */
export function extensionCostFactor(slug: string | undefined, inputs: Record<string, any> = {}, visualizationType?: string): number {
    if (!slug) return 1;
    if (slug === 'image-to-video') return inputs.videoEngine === 'cinematic' ? 1.6 : 1;
    // A detail shot always renders premium (Nano Banana 2.1 at 2K).
    if (slug === 'product-closeup' && inputs.shot === 'detail') return 2;
    if (!PREMIUM_RECIPES.has(slug)) return 1;
    if (inputs.quality === 'standard') return 1;
    const studio = inputs.use3dCamera === true || inputs.use3dCamera === 'true';
    if (slug === 'from-reference') return inputs.matchStrength === 'loose' && studio ? 2 : 1;
    if (slug === 'lifestyle-scene') return studio ? 2 : 1;
    // product-closeup: the surface Room Scene, or any shot explicitly asked to be premium.
    if (visualizationType === 'surface') return inputs.surfaceMode === 'applied' && studio ? 2 : 1;
    return inputs.quality === 'premium' && studio ? 2 : 1;
}
